import { useMutation } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useState } from 'react'

import { createIdea } from '../api/createIdea'
import type { RoadmapPost } from '../api/types'

export type SubmitIdeaModalProps = {
  open: boolean
  onClose: () => void
  onCreated: (post: RoadmapPost) => void
}

export const SubmitIdeaModal = ({
  open,
  onClose,
  onCreated,
}: SubmitIdeaModalProps): JSX.Element | null => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      createIdea({
        title,
        description,
        authorEmail: email.trim() ? email.trim() : null,
      }),
    onSuccess: (post) => {
      setTitle('')
      setDescription('')
      setEmail('')
      onCreated(post)
    },
  })

  if (!open) {
    return null
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-dune-text/40 px-4 pb-4 pt-12 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl border border-dune-border bg-white p-6 shadow-face-strong sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.18em] text-dune-accent">
              Nova ideia
            </p>
            <h2 className="mt-1 font-display text-2xl font-light tracking-tight text-dune-text">
              Enviar uma sugestão
            </h2>
          </div>

          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-full border border-dune-border p-1.5 text-dune-text-secondary transition-colors hover:border-dune-accent hover:text-dune-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            mutation.mutate()
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-xs uppercase tracking-wider text-dune-text-secondary">
              Título
            </label>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              required
              placeholder="Ex: Cartão de débito em stablecoin"
              className="h-12 w-full rounded-2xl border border-dune-border bg-white px-4 font-sans text-sm text-dune-text outline-none placeholder:text-[#C4C4CC] focus:border-dune-accent focus:ring-2 focus:ring-dune-accent/20"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="font-display text-xs uppercase tracking-wider text-dune-text-secondary">
              Descrição
            </label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={4000}
              rows={4}
              placeholder="Explique o problema que isso resolve e como você usaria."
              className="w-full rounded-2xl border border-dune-border bg-white px-4 py-3 font-sans text-sm text-dune-text outline-none placeholder:text-[#C4C4CC] focus:border-dune-accent focus:ring-2 focus:ring-dune-accent/20"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="font-display text-xs uppercase tracking-wider text-dune-text-secondary">
              Email (opcional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="voce@email.com"
              className="h-12 w-full rounded-2xl border border-dune-border bg-white px-4 font-sans text-sm text-dune-text outline-none placeholder:text-[#C4C4CC] focus:border-dune-accent focus:ring-2 focus:ring-dune-accent/20"
            />
            <p className="text-xs text-dune-text-secondary">
              Para avisarmos quando a ideia for entregue.
            </p>
          </div>

          {mutation.isError ? (
            <p className="text-sm text-signal-red">
              {(mutation.error as Error).message}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="mt-2 w-full rounded-2xl bg-dune-accent py-4 font-display font-medium text-white transition-colors hover:bg-[#E67700] disabled:bg-dune-border disabled:text-[#9CA3AF]"
          >
            {mutation.isPending ? 'Enviando...' : 'Enviar ideia'}
          </button>
        </form>
      </div>
    </div>
  )
}
